import { graphql, Link } from 'gatsby'
import React, { useContext } from 'react'

import { CustomLink, MarkdownComponent, SharePost } from '@/components'
import {
    ListWithImage,
    FeaturesBox,
    Feature,
} from '@/components/single-case-study-layout'
import { themes } from '@/consts'
import { ThemeContext } from '@/context'
import { dateParser } from '@/helpers'
import { ArrowBackIconComponent } from '@/icons'
import { ButtonSecondary, Image, Label } from '@/uikit'

interface Props {
    data: GatsbyTypes.CaseStudyTemplateQuery
}

const CaseStudyPage: React.FC<Props> = ({ data }) => {
    const {
        state: { theme },
    } = useContext(ThemeContext)
    const isDark = theme === themes.dark
    const { rawMarkdownBody, frontmatter } = data?.markdownRemark || {}

    return (
        <section className="pt-12 pb-23 lg:pt-20 lg:pb-30">
            <div className="max-w-full">
                <div className="max-w-540 mx-auto lg:max-w-770 sxl:max-w-1000 lg:px-7.5 lg:grid lg:grid-cols-2 lg:auto-rows-min lg:gap-x-7.5">
                    {/* Title */}
                    <div className="relative mb-10.5 mx-7.5 sm:mx-0 lg:mb-7.5">
                        <Label>Case study</Label>
                        <p className="mt-4 mb-7.5 text-text text-40 dark:text-gray-darker font-ilisarniq font-bold leading-big tracking-tightest lg:text-5xl">
                            {frontmatter?.title}
                        </p>
                        <Link to="/case-studies" className="absolute -left-20 top-10 hidden md:block">
                            <ArrowBackIconComponent />
                        </Link>
                    </div>
                    {/* Image */}
                    <div className="bg-body-bg2 dark:bg-body-dm xmd:max-w-col mx-auto row-span-2 xmd:rounded-semiXl">
                        {frontmatter?.cs_image_light && (
                            <div className="min-h-270 w-full dark:hidden max-w-full flex items-stretch justify-center h-full">
                                <Image src={frontmatter.cs_image_light} alt="post image" className="xmd:rounded-semiXl object-cover" />
                            </div>
                        )}
                        {frontmatter?.cs_image_dark && (
                            <div className="min-h-270 w-full dark:flex hidden max-w-full items-stretch justify-center h-full">
                                <Image src={frontmatter.cs_image_dark} alt="post image" className="xmd:rounded-semiXl object-cover" />
                            </div>
                        )}
                    </div>
                    {/* Info */}
                    <div className="flex flex-wrap gap-x-5 mt-7.5 mb-15 mx-7.5 sm:mx-0 lg:row-start-2 lg:my-0">
                        <div className="flex-1">
                            <p className="text-black-lighter opacity-70 font-inter leading-5 mb-4 font-light dark:text-text-dm dark:text-opacity-70">
                                Industry:
                            </p>
                            <h3 className="font-inter font-semibold text-lg leading-medium dark:text-gray-darker">
                                {frontmatter?.industry}
                            </h3>
                        </div>
                        <div className="flex-1">
                            <p className="text-black-lighter opacity-70 font-inter leading-5 mb-4 font-light dark:text-text-dm dark:text-opacity-70">
                                Region:
                            </p>
                            <h3 className="font-inter font-semibold text-lg leading-medium dark:text-gray-darker">
                                {frontmatter?.region}
                            </h3>
                        </div>
                        {frontmatter?.cta_link && (
                            <div className="w-full flex-auto flex mx-auto mt-15 lg:mt-9">
                                <CustomLink
                                    {...frontmatter.cta_link[0]}
                                    newTab={frontmatter.cta_link[0].new_tab || false}
                                >
                                    <ButtonSecondary>
                                        {frontmatter.cta_link[0].title}
                                    </ButtonSecondary>
                                </CustomLink>
                            </div>
                        )}
                    </div>
                </div>
                <div className="max-w-540 grid mx-auto lg:max-w-1060 lg:px-7.5 lg:gap-x-7.5">
                    {/* Content */}
                    <div className="mx-7.5 font-inter sm:mx-0 lg:mt-30 lg:max-w-770 lg:place-self-center">
                        {rawMarkdownBody && (
                            <MarkdownComponent
                                externalComponents={{
                                    ListWithImage,
                                    FeaturesBox,
                                    Feature,
                                }}
                            >
                                {rawMarkdownBody}
                            </MarkdownComponent>
                        )}
                        {frontmatter && <SharePost title={frontmatter.title} excerpt={frontmatter.excerpt} />}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default CaseStudyPage
export const caseStudyQuery = graphql`
    query CaseStudyTemplateQuery($id: String!) {
        markdownRemark(id: { eq: $id }) {
            id
            rawMarkdownBody
            frontmatter {
                title
                url
                industry
                region
                excerpt
                cta_link {
                    type
                    title
                    url
                    new_tab
                }
                cs_image_light {
                    childImageSharp {
                        gatsbyImageData(placeholder: BLURRED, aspectRatio: 1.77, formats: [WEBP])
                    }
                    extension
                    publicURL
                }
                cs_image_dark {
                    childImageSharp {
                        gatsbyImageData(placeholder: BLURRED, aspectRatio: 1.77, formats: [WEBP])
                    }
                    extension
                    publicURL
                }
                meta_tags {
                    title
                    description
                    og_image {
                        id
                    }
                }
            }
        }
    }
`
